import { useEffect, useState } from 'react';
import APIService from './api';

const BASE = 'https://besoins-tat-production.up.railway.app';

// ── Feature flags par organisation (GET/PUT /config/features) ──
// Cache module unique + abonnés React : les écrans/boutons se masquent quand
// enabled === false. Par défaut (flags non chargés ou clé absente) : ACTIVÉ,
// pour ne jamais bloquer l'app si l'endpoint est indisponible.
let cache = null;
const listeners = new Set();
const notify = () => listeners.forEach(l => l(cache));
// UN SEUL APPEL EN VOL. Le cache est module-unique, mais il est encore vide
// pendant que la page monte : sur un besoin qui porte vingt candidats, chaque
// ligne monte `useFeature('matrice')`, `useModuleApp('nda')`,
// `useModuleApp('docsign')`… et chacune voyait `cache == null` et lançait SA
// requête. Soixante appels simultanés à /config/features à l'ouverture d'un
// besoin — la même réponse, soixante fois, et un serveur qu'on bombarde au
// pire moment. Les abonnés attendent désormais la même promesse.
let enVol = null;

const parse = (j) => {
  const d = j?.data || j?.features || j;
  return (d && typeof d === 'object' && !Array.isArray(d)) ? d : null;
};

export async function loadFeatures() {
  if (enVol) return enVol;
  enVol = (async () => {
    try {
      // Sans jeton, on ne DEMANDE pas : un « Bearer null » est un refus
      // d'authentification pour le serveur, et l'app se ferait éjecter par sa
      // propre lecture de réglages d'affichage. Tout reste activé, comme hors
      // ligne.
      const jeton = APIService.getToken();
      if (!jeton) return cache;
      const r = await fetch(`${BASE}/config/features`, { headers: { Authorization: `Bearer ${jeton}` } });
      if (!r.ok) return cache;
      const d = parse(await r.json().catch(() => null));
      if (d) { cache = d; notify(); }
    } catch { /* offline / pas encore déployé → tout reste activé */ }
    return cache;
  })().finally(() => { enVol = null; });
  return enVol;
}

// PUT fusionnant : { flags: { cle: bool } } — la réponse renvoie l'état complet.
export async function saveFeatures(flags) {
  const r = await fetch(`${BASE}/config/features`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${APIService.getToken()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ flags }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
  const d = parse(j);
  if (d) { cache = d; notify(); }
  return cache;
}

// Masquage UI : on lit `enabled_for_me` (jamais `enabled`) — un admin a toujours
// enabled_for_me:true et voit tout, même une fonctionnalité coupée pour les users.
export function isEnabled(key) {
  const f = cache?.[key];
  return !f || f.enabled_for_me !== false;
}

export function useFeatures() {
  const [f, setF] = useState(cache);
  useEffect(() => {
    const l = (v) => setF(v ? { ...v } : v);
    listeners.add(l);
    if (cache == null) loadFeatures();
    return () => listeners.delete(l);
  }, []);
  return f;
}

export function useFeature(key) {
  const f = useFeatures();
  const e = f?.[key];
  return !e || e.enabled_for_me !== false; // enabled_for_me = bypass admin inclus
}

// Fonctionnalité OPT-IN (désactivée par défaut) : masquée tant que le flag n'est
// pas PRÉSENT et activé. Bypass admin conservé (enabled_for_me), comme les autres
// features — mais l'absence du flag = masqué (contrairement à useFeature qui
// laisse passer par défaut). Pour les nouveautés qu'on n'expose qu'une fois prêtes.
export function useFeatureOptIn(key) {
  const f = useFeatures();
  const e = f?.[key];
  return !!e && e.enabled_for_me !== false;
}

// ── Applications Marketplace (ex. sourcia) ──
// Contrairement aux features classiques, une APP suit l'état d'activation ORG
// (`enabled`) pour TOUT LE MONDE — pas de bypass admin : app désactivée sur la
// Marketplace = boutons/onglets cachés, y compris pour l'admin. Et une app
// jamais provisionnée (flag absent) est désactivée par défaut (opt-in).
export function useFeatureApp(key) {
  const f = useFeatures();
  const e = f?.[key];
  return !!e && e.enabled !== false;
}

// ── Droits par utilisateur sur les apps Marketplace ──
// user.modules = { crm: false, … } : on RETIRE des accès — clé absente ou objet
// absent = accès accordé (une app activée au Marketplace est visible par défaut).
// L'admin voit toujours tout (il gère les droits, il ne peut pas s'enfermer
// dehors). L'application côté API reste au backend (défense en profondeur).
export const moduleAllowed = (user, key) => {
  if (user?.admin) return true; // le back garantit modules = {} pour un admin — double sécurité
  const m = user?.modules;
  if (!m || typeof m !== 'object') return true;
  return m[key] !== false;
};

// App Marketplace POUR l'utilisateur connecté : activée au niveau org ET non
// retirée à ce compte (Config → Utilisateurs → Modules). À utiliser pour tout
// écran/bouton porté par un module — règle unique du masquage navigation.
export function useModuleApp(key) {
  const on = useFeatureApp(key);
  let user = null;
  try { user = APIService.getCurrentUser(); } catch { /* non connecté */ }
  return on && moduleAllowed(user, key);
}

// ── Défense en profondeur : 403 { code: 'feature_disabled' } ──
// Un onglet resté ouvert pendant la bascule peut encore appeler un endpoint
// désactivé : on affiche le message backend tel quel (événement écouté par App)
// et on recharge les flags pour masquer l'UI concernée.
let patched = false;
export function installFeatureDisabledInterceptor() {
  if (patched || typeof window === 'undefined') return;
  patched = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await orig(...args);
    if (res.status === 403) {
      try {
        const j = await res.clone().json();
        if (j && j.code === 'feature_disabled') {
          window.dispatchEvent(new CustomEvent('erpia:feature-disabled', { detail: j }));
          loadFeatures();
        }
      } catch { /* corps non JSON */ }
    } else if (res.status === 401) {
      // Compte désactivé en cours de session → déconnexion globale immédiate.
      try {
        const j = await res.clone().json();
        if (j && j.code === 'account_disabled') {
          window.dispatchEvent(new CustomEvent('erpia:account-disabled', { detail: j }));
        }
      } catch { /* corps non JSON */ }
    }
    return res;
  };
}
